/**
 * ConfidenceScore - Visual confidence indicator with circular progress ring
 *
 * Features:
 * - Circular progress ring with percentage
 * - Color coding based on confidence level:
 *   - >= 0.9: emerald (high confidence)
 *   - >= 0.7: amber (medium confidence)
 *   - < 0.7: red (needs review)
 * - Tooltip with explanation
 * - Framer Motion entrance animation
 */

'use client';

import { motion } from 'framer-motion';
import React from 'react';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import { getConfidenceColors } from '../../utils/confidenceUtils';
import type { ConfidenceScoreProps } from './types';

const SIZE_CONFIG = {
  sm: { size: 32, strokeWidth: 3, fontSize: 'text-[10px]' },
  md: { size: 44, strokeWidth: 4, fontSize: 'text-xs' },
  lg: { size: 56, strokeWidth: 5, fontSize: 'text-sm' },
} as const;

export const ConfidenceScore: React.FC<ConfidenceScoreProps> = ({
  confidence,
  size = 'md',
  showLabel = false,
  className,
}) => {
  const config = SIZE_CONFIG[size];
  const radius = (config.size - config.strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - confidence);
  const percentage = Math.round(confidence * 100);
  const colors = getConfidenceColors(confidence);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          className={cn('flex items-center gap-2', className)}
        >
          <div
            className="relative flex items-center justify-center"
            style={{ width: config.size, height: config.size }}
          >
            {/* Background circle */}
            <svg
              className="absolute inset-0 -rotate-90"
              width={config.size}
              height={config.size}
            >
              <circle
                cx={config.size / 2}
                cy={config.size / 2}
                r={radius}
                fill="none"
                strokeWidth={config.strokeWidth}
                className="stroke-zinc-200"
              />
              {/* Progress circle */}
              <motion.circle
                cx={config.size / 2}
                cy={config.size / 2}
                r={radius}
                fill="none"
                strokeWidth={config.strokeWidth}
                strokeLinecap="round"
                className={colors.stroke}
                strokeDasharray={circumference}
                initial={{ strokeDashoffset: circumference }}
                animate={{ strokeDashoffset }}
                transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
              />
            </svg>
            {/* Percentage text */}
            <span
              className={cn(
                'font-semibold tabular-nums',
                config.fontSize,
                colors.text,
              )}
            >
              {percentage}
            </span>
          </div>
          {showLabel && (
            <span className={cn('text-xs font-medium', colors.text)}>
              {colors.label}
            </span>
          )}
        </motion.div>
      </TooltipTrigger>
      <TooltipContent side="top" className="text-xs">
        <p className="font-medium">{colors.label}</p>
        <p className="text-zinc-400">{percentage}% confidence score</p>
      </TooltipContent>
    </Tooltip>
  );
};

export default ConfidenceScore;
